import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
    AlertTriangle, Bell, Check, Download, FileSpreadsheet, Loader2, Mail, RefreshCw, Trash2, Upload,
} from "lucide-react";

export default function Settings() {
    const [prefs, setPrefs] = useState(null);
    const [presets, setPresets] = useState([]);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState({});
    const fileRef = useRef(null);

    const loadAll = async () => {
        const [p, pr, st] = await Promise.all([
            api.get("/alerts/preferences"),
            api.get("/presets"),
            api.get("/data/status"),
        ]);
        setPrefs(p.data);
        setPresets(pr.data.items);
        setStatus(st.data);
    };
    useEffect(() => { loadAll(); }, []);

    const setBusyKey = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

    const togglePref = async (v) => {
        setBusyKey("pref", true);
        try {
            const res = await api.put("/alerts/preferences", { email_on_raise: v });
            setPrefs((p) => ({ ...p, ...res.data }));
            toast.success(v ? "Email alerts enabled" : "Email alerts disabled");
        } catch { toast.error("Could not save preferences"); }
        finally { setBusyKey("pref", false); }
    };

    const sendTest = async () => {
        setBusyKey("test", true);
        try {
            await api.post("/alerts/test");
            toast.success("Test email sent — check your inbox");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test email failed");
        } finally { setBusyKey("test", false); }
    };

    const deletePreset = async (id) => {
        try {
            await api.delete(`/presets/${id}`);
            setPresets((ps) => ps.filter((p) => p.preset_id !== id));
            toast.success("Preset deleted");
        } catch { toast.error("Could not delete preset"); }
    };

    const refreshData = async () => {
        setBusyKey("refresh", true);
        try {
            const res = await api.post("/data/refresh");
            toast.success(res.data.message || "Refresh started");
        } catch { toast.error("Could not start refresh"); }
        finally { setBusyKey("refresh", false); }
    };

    const downloadTemplate = () => {
        window.location.href = `${api.defaults.baseURL}/csv/template`;
    };

    const uploadCsv = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!window.confirm(
            `This will REPLACE the entire stock universe (currently ${status?.total ?? "—"} tickers) with the CSV content. Continue?`
        )) {
            e.target.value = "";
            return;
        }
        const form = new FormData();
        form.append("file", file);
        setBusyKey("upload", true);
        try {
            const res = await api.post("/csv/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Replaced universe with ${res.data.inserted} stocks`);
            await loadAll();
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (Array.isArray(d?.errors)) toast.error(d.errors[0]);
            else toast.error(typeof d === "string" ? d : "Upload failed");
        } finally {
            setBusyKey("upload", false);
            e.target.value = "";
        }
    };

    const resetUniverse = async () => {
        if (!window.confirm("Restore the bundled seed universe? Your current CSV-loaded data will be lost.")) return;
        setBusyKey("reset", true);
        try {
            const res = await api.post("/csv/reset");
            toast.success(`Restored ${res.data.inserted} seed stocks`);
            await loadAll();
        } catch { toast.error("Reset failed"); }
        finally { setBusyKey("reset", false); }
    };

    const importSheet = async () => {
        if (!window.confirm("Import from the Community Dividend Finder Google Sheet? This will REPLACE the current universe.")) return;
        setBusyKey("sheet", true);
        try {
            const res = await api.post("/sheet/import", {});
            const per = res.data.per_tab || {};
            toast.success(`Imported ${res.data.inserted} stocks (K:${per.Kings || 0} · A:${per.Aristocraten || 0} · Co:${per.Contenders || 0} · Ch:${per.Challengers || 0})`);
            await loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sheet import failed");
        } finally { setBusyKey("sheet", false); }
    };

    return (
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10 py-10 space-y-8">
            <div className="animate-fade-up">
                <div className="overline">Account</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Settings</h1>
            </div>

            {/* ALERTS */}
            <section className="rounded-2xl border border-border bg-background p-6" data-testid="settings-alerts">
                <SectionHeader icon={Bell} title="Dividend alerts" subtitle="Get an email when a stock on your watchlist raises its dividend." />

                {!prefs ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                ) : (
                    <div className="space-y-4">
                        {!prefs.email_enabled && (
                            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="alerts-disabled-banner">
                                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                                <div>
                                    <div className="font-semibold text-amber-900">Email service not configured</div>
                                    <div className="text-amber-800 mt-0.5">
                                        Add your <code className="px-1 py-0.5 rounded bg-amber-100 text-amber-900">RESEND_API_KEY</code> to{" "}
                                        <code className="px-1 py-0.5 rounded bg-amber-100 text-amber-900">/app/backend/.env</code> and restart the backend.
                                        Get a key at <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline">resend.com</a>.
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between p-4 rounded-xl bg-muted/40 border border-border">
                            <div>
                                <Label className="text-sm font-medium">Email me when a watched stock raises its dividend</Label>
                                <div className="text-xs text-muted-foreground mt-1">Sent to <span className="font-medium">{prefs.email}</span></div>
                            </div>
                            <Switch
                                checked={prefs.email_on_raise}
                                onCheckedChange={togglePref}
                                disabled={busy.pref}
                                data-testid="toggle-raise-alerts"
                            />
                        </div>

                        <div>
                            <Button variant="outline" size="sm" onClick={sendTest} disabled={busy.test || !prefs.email_enabled} data-testid="test-email-btn">
                                {busy.test ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                                Send test email
                            </Button>
                            <span className="ml-2 text-xs text-muted-foreground">Daily check runs at 09:00 UTC.</span>
                        </div>
                    </div>
                )}
            </section>

            {/* DATA REFRESH */}
            <section className="rounded-2xl border border-border bg-background p-6" data-testid="settings-data">
                <SectionHeader icon={RefreshCw} title="Live data" subtitle="Pull the latest dividends and fundamentals from Yahoo Finance." />
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm text-muted-foreground">
                        {status ? (
                            <>Universe: <span className="font-medium text-foreground tabular-nums">{status.total} tickers</span> ·{" "}
                                Last updated: <span className="font-medium text-foreground">{formatDate(status.last_updated_at)}</span></>
                        ) : "Loading…"}
                    </div>
                    <Button onClick={refreshData} disabled={busy.refresh} data-testid="refresh-data-btn">
                        {busy.refresh ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Refresh now (yfinance)
                    </Button>
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                    Manual refresh runs in the background and takes ~1–2 min for 50+ tickers. Auto-refresh runs daily at 09:00 UTC.
                </div>
            </section>

            {/* CSV */}
            <section className="rounded-2xl border border-border bg-background p-6" data-testid="settings-csv">
                <SectionHeader icon={Upload} title="Stock universe" subtitle="Import from the Community Dividend Finder sheet, upload a CSV, or restore the default seed." />
                <div className="flex flex-wrap items-center gap-3">
                    <Button onClick={importSheet} disabled={busy.sheet} data-testid="import-sheet-btn">
                        {busy.sheet ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                        Import from Google Sheet
                    </Button>
                    <Button variant="outline" onClick={downloadTemplate} data-testid="csv-template-btn">
                        <Download className="h-4 w-4" />
                        Download CSV template
                    </Button>
                    <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy.upload} data-testid="csv-upload-btn">
                        {busy.upload ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        Upload CSV
                    </Button>
                    <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={uploadCsv} data-testid="csv-file-input" />
                    <Button variant="ghost" onClick={resetUniverse} disabled={busy.reset} data-testid="csv-reset-btn">
                        Restore default seed
                    </Button>
                </div>
                <div className="mt-3 text-xs text-muted-foreground max-w-2xl leading-relaxed">
                    The Google Sheet import is the easiest path — it pulls the full Kings/Aristocrats/Contenders/Challengers universe directly.
                    All three options REPLACE the current universe.
                </div>
            </section>

            {/* PRESETS */}
            <section className="rounded-2xl border border-border bg-background p-6" data-testid="settings-presets">
                <SectionHeader icon={Check} title="Saved screener presets" subtitle="Quickly load filter combinations you use often." />
                {presets.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                        No saved presets yet. Save one from the screener (Save preset button next to the filters).
                    </div>
                ) : (
                    <ul className="divide-y divide-border" data-testid="presets-list">
                        {presets.map((p) => (
                            <li key={p.preset_id} className="flex items-center justify-between py-3" data-testid={`preset-row-${p.preset_id}`}>
                                <div>
                                    <div className="font-medium">{p.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {summarisePreset(p)} · saved {formatDate(p.created_at)}
                                    </div>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => deletePreset(p.preset_id)}
                                    className="text-muted-foreground hover:text-destructive"
                                    data-testid={`delete-preset-${p.preset_id}`}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
    return (
        <div className="flex items-start gap-3 mb-5">
            <div className="h-9 w-9 rounded-xl bg-accent grid place-items-center shrink-0">
                <Icon className="h-4 w-4 text-accent-foreground" />
            </div>
            <div>
                <h2 className="font-heading text-lg font-semibold">{title}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>
            </div>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
}

function summarisePreset(p) {
    const parts = [];
    const f = p.filters || {};
    if (f.categories?.length) parts.push(`${f.categories.length} categories`);
    if (f.sectors?.length) parts.push(`${f.sectors.length} sectors`);
    if (f.min_yield) parts.push(`yield ≥ ${f.min_yield}%`);
    if (f.min_streak) parts.push(`streak ≥ ${f.min_streak}y`);
    if (f.search) parts.push(`"${f.search}"`);
    return parts.length ? parts.join(" · ") : "All filters default";
}
